import { computed, ref, watch, ComputedRef, Ref } from 'vue';
import { getPaginationRowModel, PaginationState } from '@tanstack/vue-table';
import { Virtualizer } from '@tanstack/vue-virtual';
import type { Emits } from '../../modernGrid.setup';
import type {
  GridRowDataType,
  GridRawRowDataType,
  GridTableInstance,
  GridTableInstanceOptions,
  PaginationOption,
} from '../../modernGrid.types';
import { INITIAL_PAGE_INDEX, INITIAL_PAGE_SIZE } from '../../modernGrid.define';

export const usePagination = <RowDataType extends GridRawRowDataType>({
  emit,
  table,
  rows,
  virtualRowManager,
  paginationInfo,
}: {
  emit: Emits<RowDataType>;
  table: Ref<GridTableInstance<RowDataType> | undefined>;
  rows: Ref<GridRowDataType<RowDataType>[] | undefined>;
  virtualRowManager: Ref<Virtualizer<HTMLElement, Element>>;
  paginationInfo: ComputedRef<PaginationOption>;
}) => {
  const useServerSidePaging = computed(
    () => paginationInfo.value.use && !paginationInfo.value.useClient,
  );
  const currentPageNum = ref(INITIAL_PAGE_INDEX + 1);
  const perDataCnt = ref<number>(paginationInfo.value.perData ?? INITIAL_PAGE_SIZE);
  const paginationState = ref<PaginationState>();
  const totalRowCount = computed<number>(() =>
    useServerSidePaging.value
      ? paginationInfo.value.total
      : table.value?.getFilteredRowModel()?.rows?.length || 0,
  );

  const updatePagination = (
    updater: PaginationState | ((state: PaginationState) => PaginationState),
  ) => {
    const newPagination =
      typeof updater === 'function'
        ? updater(
            paginationState.value ||
              table.value?.getState().pagination || {
                pageIndex: currentPageNum.value - 1,
                pageSize: perDataCnt.value,
              },
          )
        : updater;
    paginationState.value = newPagination;
    emit('change-pagination', {
      current: newPagination.pageIndex + 1,
      perDataCnt: newPagination.pageSize,
      total: totalRowCount.value,
    });
    virtualRowManager.value?.scrollToIndex(0);
  };

  const paginationOptions = computed<Partial<GridTableInstanceOptions<RowDataType>>>(() => ({
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: !paginationInfo.value.use || useServerSidePaging.value,
    autoResetPageIndex: false,
    rowCount: useServerSidePaging.value ? rows.value?.length : undefined,
    totalRowCount: useServerSidePaging.value ? paginationInfo.value.total : undefined,
    onPaginationChange: updatePagination,
  }));

  watch([currentPageNum, perDataCnt], ([currentPage, perData]) => {
    table.value?.setPagination({
      pageIndex: currentPage - 1,
      pageSize: perData,
    });
  });

  return {
    currentPageNum,
    perDataCnt,
    paginationState,
    totalRowCount,
    paginationOptions,
  };
};
